<template>
    <div
        class="grid grid-cols-6 grid-rows-2 divide-x divide-y workpost-paint-screen-page router-view-full lg:divide-y-0 divide-slate-400 uses-tailwind"
    >
        <div
            class="order-1 col-span-3 row-span-1 px-4 lg:row-span-2 lg:col-span-2"
        >
            <div>
                <div class="flex flex-col">
                    <div class="flex items-center gap-2">
                        <ui5-label
                            class="text-3xl"
                            for="optimized-paint-list-id"
                            required
                        >
                            {{
                                trans('actions.scan_subject', {
                                    subject: trans(
                                        'paint_lists.paint_list'
                                    ).toLowerCase(),
                                })
                            }}:
                        </ui5-label>
                        <spinner
                            v-if="state.fetchingData"
                            class="h-6"
                        />
                    </div>
                    <ui5-input
                        class="text-3xl bg-white h-[64px] w-full"
                        id="optimized-paint-list-id"
                        type="search"
                        show-clear-icon="true"
                        :value="scannedPaintListId || ''"
                        @change="scannedPaintListId = $event.target.value"
                        :placeholder="
                            trans('actions.scan_subject', {
                                subject: trans(
                                    'paint_lists.paint_list'
                                ).toLowerCase(),
                            })
                        "
                    />
                </div>
                <div
                    class="flex flex-col mt-4"
                    :class="{
                        'opacity-10 pointer-events-none': !paintList,
                    }"
                >
                    <ui5-label
                        class="text-3xl"
                        for="beams"
                        required
                    >
                        {{ trans('paint_lists.amount_of_beams') }}:
                    </ui5-label>
                    <div class="flex items-center gap-2">
                        <ui5-input
                            class="text-3xl h-[64px] flex-1"
                            :class="{
                                'bg-orange-500/20': alteredBeams,
                                'bg-white': !alteredBeams,
                            }"
                            id="beams"
                            type="number"
                            min="1"
                            :value="form.beams"
                            @change="form.beams = $event.target.value"
                        />
                        <ui5-button
                            class="bg-zinc-900 hover:bg-zinc-800 focus:bg-zinc-800 transition-all cursor-pointer text-white text-3xl aspect-square h-[64px]"
                            @click="form.beams += 1"
                        >
                            +
                        </ui5-button>
                        <ui5-button
                            class="bg-zinc-900 hover:bg-zinc-800 focus:bg-zinc-800 transition-all cursor-pointer text-white text-3xl aspect-square h-[64px]"
                            @click="form.beams > 1 ? (form.beams -= 1) : null"
                        >
                            -
                        </ui5-button>
                    </div>
                </div>
            </div>
            <div
                class="mt-8"
                :class="{
                    'opacity-10 pointer-events-none': !paintList,
                }"
            >
                <div class="flex items-center gap-2 text-xl">
                    <p class="text-zinc-500">{{ trans('common.name') }}:</p>
                    <p>{{ userName }}</p>
                </div>
                <div class="flex items-center gap-2 mt-2 text-xl">
                    <p class="text-zinc-500">
                        {{ trans('measurements.squared.meter') }}:
                    </p>
                    <p v-if="paintList">{{ paintList.area }}</p>
                </div>
                <div class="flex items-center gap-2 text-xl">
                    <p class="text-zinc-500">
                        {{ trans('paint_lists.item_count') }}:
                    </p>
                    <p v-if="paintList">{{ paintList.itemCount }}</p>
                </div>
            </div>
            <div
                class="mt-8"
                :class="{
                    'opacity-10 pointer-events-none': !paintList,
                }"
            >
                <div
                    class="p-2 space-y-2 border-2 rounded-lg border-zinc-500"
                    :class="{
                        'bg-green-600/40':
                            scannedPowderId &&
                            scannedPowderId === (powder ? powder.code : null),
                        'bg-red-800/40':
                            scannedPowderId &&
                            scannedPowderId !== (powder ? powder.code : null),
                    }"
                >
                    <div class="flex items-center gap-2">
                        <ui5-label
                            class="text-3xl"
                            for="powder-id"
                            required
                        >
                            {{
                                trans('actions.scan_subject', {
                                    subject:
                                        trans('powders.powder').toLowerCase(),
                                })
                            }}:
                        </ui5-label>
                        <ui5-input
                            class="text-3xl bg-white h-[64px] flex-1"
                            id="powder-id"
                            type="search"
                            show-clear-icon="true"
                            :value="scannedPowderId || ''"
                            @change="scannedPowderId = $event.target.value"
                            :placeholder="
                                trans('actions.scan_subject', {
                                    subject:
                                        trans('powders.powder').toLowerCase(),
                                })
                            "
                        />
                    </div>
                    <div class="flex items-center gap-2 text-2xl">
                        <p class="text-zinc-500">
                            {{ trans('common.location') }}:
                        </p>
                        <p v-if="powder">{{ powder.location }}</p>
                        <p class="text-zinc-500">
                            {{ trans('profiles.profile') }}:
                        </p>
                        <p v-if="powder">{{ powder.code }}</p>
                    </div>
                    <div class="flex items-center gap-2 text-3xl">
                        <p class="text-zinc-500">{{ trans('colors.ral') }}:</p>
                        <p v-if="color">
                            {{ `${color.ralNumber} ${color.ralType}` }}
                        </p>
                    </div>
                    <div class="flex items-center gap-2 text-3xl">
                        <p class="text-zinc-500">
                            {{ trans('colors.powder_code') }}:
                        </p>
                        <p v-if="color">{{ color.code }}</p>
                    </div>
                    <div class="flex items-center gap-2 text-2xl">
                        <p class="text-zinc-500">
                            {{ trans('colors.producer') }}:
                        </p>
                        <p v-if="color">{{ color.producer }}</p>
                    </div>
                    <div class="flex items-center gap-2 text-2xl">
                        <p class="text-zinc-500">
                            {{ trans('measurements.unit') }}:
                        </p>
                        <p v-if="powder">{{ powder.unit }}</p>
                    </div>
                    <div class="flex flex-col gap-2">
                        <ui5-label
                            class="text-2xl"
                            for="color-sort"
                            required
                        >
                            {{ trans('colors.sort') }}:
                            {{ trans('colors.sort_help_text') }}
                        </ui5-label>
                        <div class="flex items-center gap-2 mt-2">
                            <ui5-input
                                class="text-2xl h-[64px] flex-1"
                                :class="{
                                    'bg-orange-500/20': alteredColorSort,
                                    'bg-white': !alteredColorSort,
                                }"
                                id="color-sort"
                                type="number"
                                show-clear-icon="true"
                                min="0"
                                :value="form.colorSort"
                                :disabled="!canStartProcessing"
                                @change="form.colorSort = $event.target.value"
                            />
                            <ui5-button
                                class="bg-zinc-900 hover:bg-zinc-800 focus:bg-zinc-800 transition-all text-white text-3xl aspect-square h-[64px]"
                                @click="form.colorSort += 1"
                                :disabled="!canStartProcessing"
                            >
                                +
                            </ui5-button>
                            <ui5-button
                                class="bg-zinc-900 hover:bg-zinc-800 focus:bg-zinc-800 transition-all text-white text-3xl aspect-square h-[64px]"
                                @click="
                                    form.colorSort > 1
                                        ? (form.colorSort -= 1)
                                        : null
                                "
                                :disabled="!canStartProcessing"
                            >
                                -
                            </ui5-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div
            class="relative order-3 col-span-3 row-span-1 px-4 lg:row-span-2 lg:col-span-2 lg:order-2"
        >
            <div
                class="absolute text-2xl font-bold text-center transform -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2 text-uppercase"
                v-show="!canStartProcessing"
            >
                <p v-if="!scannedPowderId && scannedPaintListId">
                    {{ trans('paint_screen.scan_powder_to_view_content') }}
                </p>
                <p v-else>
                    {{ trans('paint_screen.scan_paint_list_to_view_content') }}
                </p>
            </div>
            <div
                :class="{
                    'opacity-10 pointer-events-none': !canStartProcessing,
                }"
            >
                <div>
                    <div class="flex items-center gap-2 text-3xl">
                        <p class="text-zinc-500">
                            {{ trans('paint_screen.current_stock') }}:
                        </p>
                        <div
                            class="self-stretch flex-grow mb-1 border-b border-dotted"
                        ></div>
                        <p :class="{ 'text-orange-500': alteredCurrentStock }">
                            {{ formatKg(calculatedCurrentStockInKg) }}
                            {{
                                trans(
                                    'measurements.units.abbreviations.kilogram'
                                )
                            }}
                        </p>
                    </div>
                    <div class="flex items-center gap-2 mt-4 text-3xl">
                        <p class="text-zinc-500">
                            {{ trans('paint_screen.spare_stock') }}:
                        </p>
                        <div
                            class="self-stretch flex-grow mb-1 border-b border-dotted"
                        ></div>
                        <p :class="{ 'text-orange-500': alteredSpareStock }">
                            {{ formatKg(calculatedSpareStockInKg) }}
                            {{
                                trans(
                                    'measurements.units.abbreviations.kilogram'
                                )
                            }}
                        </p>
                    </div>
                    <div class="flex items-center gap-2 mt-5 text-3xl">
                        <p class="text-zinc-500">
                            {{ trans('paint_screen.reserved_stock') }}:
                        </p>
                        <div
                            class="self-stretch flex-grow mb-1 border-b border-dotted"
                        ></div>
                        <p>
                            {{ formatKg(reservedStockInKg) }}
                            {{
                                trans(
                                    'measurements.units.abbreviations.kilogram'
                                )
                            }}
                        </p>
                    </div>
                    <div class="flex items-center gap-2 mt-5 text-3xl">
                        <p class="text-zinc-500">
                            {{ trans('paint_screen.box_weight') }}:
                        </p>
                        <div
                            class="self-stretch flex-grow mb-1 border-b border-dotted"
                        ></div>
                        <p>
                            {{ formatKg(weightBoxInKg) }}
                            {{
                                trans(
                                    'measurements.units.abbreviations.kilogram'
                                )
                            }}
                        </p>
                    </div>
                    <div class="flex items-center gap-2 mt-5 text-3xl">
                        <ui5-label
                            class="text-3xl"
                            for="weighed"
                        >
                            {{ trans('common.weighed') }}:
                        </ui5-label>
                        <div
                            class="self-stretch flex-grow mb-1 border-b border-dotted"
                        ></div>
                        <div class="flex items-center gap-2">
                            <ui5-input
                                class="text-3xl bg-white text-right h-[64px]"
                                id="weighed"
                                type="number"
                                min="0"
                                :value="weighed"
                                @change="weighed = $event.target.value"
                            />
                        </div>
                        <p>
                            {{
                                trans(
                                    'measurements.units.abbreviations.kilogram'
                                )
                            }}
                        </p>
                    </div>
                </div>
                <div class="flex items-center justify-end gap-3 mt-5">
                    <button
                        v-show="
                            actions.find((action) => action.type === 'weigh')
                        "
                        @click="revertAction('weigh')"
                        class="bg-zinc-900 h-[64px] hover:bg-zinc-800 focus:bg-zinc-800 transition-all cursor-pointer text-3xl text-white p-4 text-center rounded-lg aspect-square"
                    >
                        <i class="fa fa-undo"></i>
                    </button>
                    <button
                        @click="addWeighAction"
                        class="bg-zinc-900 h-[64px] hover:bg-zinc-800 focus:bg-zinc-800 transition-all cursor-pointer text-3xl text-white p-4 text-center rounded-lg"
                    >
                        {{ trans('actions.process') }}
                    </button>
                </div>
                <div class="grid grid-cols-2 gap-4 mt-10">
                    <div
                        v-if="newBoxOptions.length == 0"
                        class="block w-full col-span-2 px-4 py-6 text-xl text-center text-red-700 border border-red-600 rounded-lg bg-red-50"
                    >
                        {{ trans('paint_screen.no_boxes') }}
                    </div>
                    <template v-else>
                        <div class="space-y-4">
                            <button
                                v-for="value in newBoxOptions"
                                @click="toggleNewBoxAction(value)"
                                class="w-full bg-zinc-50 border border-zinc-900 hover:bg-zinc-100 focus:bg-zinc-100 transition-all text-2xl text-black px-4 h-[80px] text-center rounded-lg cursor-pointer"
                                :class="{
                                    '!bg-zinc-900 !hover:bg-zinc-900 !focus:bg-zinc-900 !text-white':
                                        actions.find(
                                            (action) =>
                                                action.type === 'new_box' &&
                                                action.value === value
                                        ),
                                }"
                            >
                                {{
                                    trans('paint_screen.box_from_stock', {
                                        weight: value,
                                        measurement: trans(
                                            'measurements.units.abbreviations.kilogram'
                                        ),
                                    })
                                }}
                            </button>
                        </div>
                        <div></div>
                    </template>
                </div>
            </div>
        </div>
        <div
            class="order-2 col-span-3 row-span-2 lg:col-span-2 lg:order-3 px-4 !border-t-0"
        >
            <div>
                <timer
                    :key="timerKey"
                    :total-seconds="timer.totalSeconds"
                    :previously-elapsed-seconds="timer.previouslyElapsedSeconds"
                    :can-pause="true"
                    :can-report-error="false"
                    :is-loading="timer.totalSeconds === 0"
                    :start-when-mounted="true"
                    @paused="onPaused"
                    @finished="onFinished"
                />
            </div>
        </div>
        <div
            v-if="showWeightError"
            class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
        >
            <div
                class="w-full max-w-md p-6 bg-white border border-red-600 rounded-lg"
            >
                <h3 class="mb-4 text-2xl font-bold text-red-700">
                    {{ trans('paint_screen.error') }}
                </h3>
                <p class="mb-4 text-xl text-red-700">
                    {{
                        trans('paint_screen.weight_validation_failed', {
                            theResponsibleManager: operatorManager,
                        })
                    }}
                </p>
                <div class="flex justify-end">
                    <button
                        class="px-4 py-2 text-xl text-white rounded-lg bg-zinc-900 hover:bg-zinc-800"
                        @click="showWeightError = false"
                    >
                        {{ trans('paint_screen.close') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import IsDebugMixin from '../mixins/is-debug';
import optimizedPaintListApi from '../api/optimizedPaintList';
import targetTimesApi from '../api/targetTimes';
import workstationApi from '../api/workstation';
import Spinner from '../components/loader/Spinner';
import { precisionRound } from '../helpers/MathHelper';
import { showInfoMessage, showErrorMessage } from '../dialogs';
import ProductTimer from '../components/scanProducts/ProductTimer';
import Timer from '../components/Timer';
import localization from '../mixins/localization';

export default {
    mixins: [IsDebugMixin, localization],
    components: { Spinner, ProductTimer, Timer },
    data() {
        return {
            workstation: null,
            scannedPaintListId: null,
            scannedPowderId: null,
            paintList: null,
            state: {
                fetchingData: false,
            },
            initialFormState: {
                colorSort: 0,
                beams: 1,
                usedPowderInGrams: 0,
                sparePowderTakenInGrams: 0,
                status: 'success',
                employeeId: 0,
            },
            weighed: null,
            actions: [],
            form: {},
            timer: {
                totalSeconds: 0,
                previouslyElapsedSeconds: 0,
            },
            boxSizes: [20, 5, 3],
            showWeightError: false, // Flag for weight validation error modal
        };
    },
    computed: {
        operatorManager: function () {
            return (
                this.$store.getters['general/getOperatorManager'] ??
                this.trans('paint_screen.the_responsible_manager')
            );
        },
        newBoxOptions() {
            const spareStock = this.calculatedSpareStockInKg;
            if (!spareStock || spareStock < 0) {
                return [];
            }
            return this.boxSizes.filter((boxSize) => boxSize <= spareStock);
        },
        timerKey() {
            return `timer-${this.paintList?.id}`;
        },
        userName() {
            return this.$store.getters['currentUser/getUserName'];
        },
        userId() {
            return this.$store.getters['currentUser/getUserId'];
        },
        color() {
            return this.paintList?.color || null;
        },
        powder() {
            return this.color?.powder || null;
        },
        currentStockInGrams() {
            return this.powder?.currentStock || 0;
        },
        currentStockInKg() {
            return precisionRound(
                (this.powder?.currentStockInGrams || 0) / 1000,
                3
            );
        },
        calculatedCurrentStockInKg() {
            let value = this.currentStockInKg;
            if (this.actions.length) {
                const lastAction = this.actions[this.actions.length - 1];
                value = lastAction.value;
            }
            return precisionRound(value, 3);
        },
        calculatedCurrentStockInGrams() {
            return precisionRound(this.calculatedCurrentStockInKg * 1000, 3);
        },
        spareStockInKg() {
            return precisionRound(
                (this.powder?.spareStockInGrams || 0) / 1000,
                3
            );
        },
        calculatedSpareStockInKg() {
            let value = this.spareStockInKg;
            this.actions.forEach((action) => {
                if (action.type === 'new_box') {
                    value -= action.value;
                }
            });
            return precisionRound(value, 3);
        },
        calculatedSpareStockInGrams() {
            return precisionRound(this.calculatedSpareStockInKg * 1000, 3);
        },
        reservedStockInKg() {
            return precisionRound(
                (this.powder?.reservedStockInGrams || 0) / 1000,
                3
            );
        },
        weightBoxInKg() {
            return precisionRound(
                (this.powder?.weightBoxInGrams || 0) / 1000,
                3
            );
        },
        canStartProcessing() {
            if (this.isDebug) {
                return true;
            }
            return (
                this.paintList &&
                this.scannedPowderId &&
                this.scannedPowderId === this.powder.code
            );
        },
        alteredBeams() {
            return (
                this.form?.beams !== this.paintList?.beams &&
                this.form?.beams != 1
            );
        },
        alteredColorSort() {
            return this.form?.colorSort !== this.color?.sort;
        },
        alteredCurrentStock() {
            return this.form?.usedPowderInGrams > 0;
        },
        alteredSpareStock() {
            return this.form?.sparePowderTakenInGrams > 0;
        },
    },
    watch: {
        scannedPaintListId: {
            handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    this.fetchDataDebounced(this);
                }
            },
        },
        actions: {
            handler(newVal, oldVal) {
                const newBoxWeight =
                    this.actions.find((action) => action.type === 'new_box')
                        ?.value || 0;
                this.form.usedPowderInGrams = precisionRound(
                    this.powder.currentStockInGrams +
                        newBoxWeight -
                        this.calculatedCurrentStockInGrams,
                    3
                );
                this.form.sparePowderTakenInGrams = precisionRound(
                    this.powder.spareStockInGrams -
                        this.calculatedSpareStockInGrams,
                    3
                );
            },
            deep: true,
        },
    },
    mounted() {
        this.fetchWorkstation();
        this.form = Object.assign({}, this.initialFormState);
        this.form.employeeId = this.userId;
        this.scannedPaintListId = this.$route.query.paintListId;
        this.listenForBarCodeScannerEvents();
    },
    methods: {
        formatKg(value) {
            return value.toFixed(3);
        },
        listenForBarCodeScannerEvents() {
            this.$bus.$on('barcode-scan', (data) => {
                if (data.startsWith('L')) {
                    this.scannedPaintListId = data;
                    this.fetchData();
                }
                if (data.startsWith('ER')) {
                    this.scannedPowderId = data;
                }
            });
        },
        resetForm() {
            this.form = Object.assign({}, this.initialFormState);
            this.form.employeeId = this.userId;
            this.weighed = null;
        },
        fetchWorkstation() {
            workstationApi
                .fetchByKey('paintshop')
                .then((response) => {
                    if (response.status !== 200 || !response.data.data) {
                        throw new Error(
                            this.trans(
                                'pages.workpost_paint_screen.errors.workstation_not_found'
                            )
                        );
                    }
                    this.workstation = response.data.data;
                })
                .catch((error) => {
                    showErrorMessage(
                        this.trans(
                            'pages.workpost_paint_screen.errors.workstation_not_found'
                        )
                    );
                });
        },
        fetchData() {
            this.state.fetchingData = true;
            this.paintList = null;
            this.resetForm();
            if (!this.scannedPaintListId) {
                this.state.fetchingData = false;
                return;
            }
            optimizedPaintListApi
                .fetchPaintScreenData(this.scannedPaintListId)
                .then((response) => {
                    if (response.status !== 200 || !response.data.data) {
                        throw new Error(
                            this.trans(
                                'pages.workpost_paint_screen.errors.paint_list_not_found'
                            )
                        );
                    }
                    this.paintList = response.data.data;
                    this.form.beams =
                        this.paintList.beams > 0 ? this.paintList.beams : 1;
                    this.form.colorSort = this.color.sort;
                    this.fetchTimerData();
                })
                .catch((error) => {
                    showErrorMessage(
                        this.trans(
                            'pages.workpost_paint_screen.errors.paint_list_not_found'
                        )
                    );
                })
                .finally(() => {
                    this.state.fetchingData = false;
                });
        },
        fetchDataDebounced: _.debounce((self) => {
            self.fetchData();
        }, 500),
        addWeighAction() {
            if (!this.paintList || !this.powder) {
                showErrorMessage(
                    this.trans(
                        'pages.workpost_paint_screen.errors.invalid_data'
                    )
                );
                return;
            }

            // Calculate the new weigh action value
            const newWeighValue =
                (this.weighed ?? 0) - (this.weightBoxInKg || 0);

            // Validate: new weigh value cannot make calculatedCurrentStockInKg > currentStockInKg unless a new box is taken
            const hasNewBoxAction = this.actions.some(
                (action) => action.type === 'new_box'
            );
            if (!hasNewBoxAction && newWeighValue > this.currentStockInKg) {
                this.showWeightError = true;
                return;
            }

            // Proceed with adding the weigh action
            this.actions = this.actions.filter(
                (action) => action.type !== 'weigh'
            );
            this.actions.push({
                type: 'weigh',
                value: newWeighValue,
            });
        },
        toggleNewBoxAction(weight) {
            const lastWeight = this.actions.find(
                (action) => action.type === 'new_box'
            )?.value;
            this.actions = this.actions.filter(
                (action) => action.type !== 'new_box'
            );
            if (lastWeight === weight) {
                return;
            }
            this.actions.push({
                type: 'new_box',
                value: weight,
            });
        },
        revertAction(type) {
            this.actions = this.actions.filter(
                (action) => action.type !== type
            );
        },
        onPaused(data) {
            this.saveTimerData(data.startedAt, data.stoppedAt);
        },
        onFinished(data) {
            this.form.status = 'success';
            this.saveTimerData(data.startedAt, data.stoppedAt);
            this.savePaintScreenData();
        },
        saveTimerData(startedAt, stoppedAt) {
            targetTimesApi
                .postHistoryForOptimizedPaintList(
                    this.workstation.id,
                    this.paintList.id,
                    this.form.employeeId,
                    startedAt,
                    stoppedAt
                )
                .then((response) => {
                    if (response.status !== 200 || !response.data.data) {
                        throw new Error(
                            this.trans(
                                'pages.workpost_paint_screen.errors.timer_save_failed'
                            )
                        );
                    }
                    showInfoMessage(
                        this.trans(
                            'pages.workpost_paint_screen.success.work_time_saved'
                        )
                    );
                })
                .catch((error) => {
                    showErrorMessage(
                        this.trans(
                            'pages.workpost_paint_screen.errors.timer_save_failed'
                        )
                    );
                });
        },
        fetchTimerData() {
            targetTimesApi
                .fetchForOptimizedPaintList(
                    this.workstation.id,
                    this.paintList.id
                )
                .then((response) => {
                    if (response.status !== 200 || !response.data.data) {
                        throw new Error(
                            this.trans(
                                'pages.workpost_paint_screen.errors.timer_load_failed'
                            )
                        );
                    }
                    const data = response.data.data;
                    this.timer.totalSeconds = data.totalTargetTimeInSeconds;
                    this.timer.previouslyElapsedSeconds =
                        data.totalElapsedSeconds;
                })
                .catch((error) => {
                    showErrorMessage(
                        this.trans(
                            'pages.workpost_paint_screen.errors.timer_load_failed'
                        )
                    );
                });
        },
        savePaintScreenData() {
            optimizedPaintListApi
                .postPaintScreenData(
                    this.paintList.id,
                    this.form.colorSort,
                    this.form.beams,
                    this.form.usedPowderInGrams,
                    this.form.sparePowderTakenInGrams,
                    this.form.status,
                    this.form.employeeId
                )
                .then((response) => {
                    if (response.status !== 200 || !response.data.success) {
                        throw new Error(
                            this.trans(
                                'pages.workpost_paint_screen.errors.paint_list_finish_failed'
                            )
                        );
                    }
                    showInfoMessage(
                        this.trans(
                            'pages.workpost_paint_screen.success.paint_list_finished'
                        )
                    );
                    this.reset();
                })
                .catch((error) => {
                    showErrorMessage(
                        this.trans(
                            'pages.workpost_paint_screen.errors.paint_list_finish_failed'
                        )
                    );
                });
        },
        reset() {
            this.scannedPaintListId = null;
            this.scannedPowderId = null;
            this.paintList = null;
            this.state.fetchingData = false;
            this.actions = [];
            this.form = Object.assign({}, this.initialFormState);
            this.timer = {
                totalSeconds: 0,
                previouslyElapsedSeconds: 0,
            };
        },
    },
};
</script>

<style scoped>
.workpost-paint-screen-page {
    height: calc(100vh - 120px);
}

ui5-input {
    margin: 0;
}
</style>
